import earcut from 'earcut';
import { latLngToLocalMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmWaterArea } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';

/**
 * Lifted a touch above the local terrain low point so the water surface sits visibly *on
 * top* of the SRTM surface (which already reports water at its real-world surface elevation
 * — there's no carved river bed in SRTM data). Without this, the terrain pokes up through
 * the water and you see the green ground colour instead of blue.
 */
const WATER_SURFACE_OFFSET = 0.25;

/**
 * `carveTerrainForWater` drops terrain vertices under water down to this depth BELOW the
 * water surface. SRTM samples non-uniformly across water (bridges, nearby buildings,
 * shoreline) so even with the surface offset, terrain mesh interpolation pokes up through
 * the water. Pushing the terrain mesh well below the water hides those pokes. Visual only —
 * the physics heightfield is left at raw SRTM so cars still drive on top of bridges and the
 * actual ground when they cross water.
 */
const WATER_CARVE_DEPTH = 0.5;

/**
 * The water surface no longer collapses to one flat plane per polygon. Instead each surface
 * point takes its height from the *local* terrain low point within this radius (metres),
 * which makes the surface drape over the ground: flat across a lake, descending down a
 * river, and hugging the bank in sliver chunks instead of floating up at the bank height.
 *
 * Taking the local *minimum* (rather than an average) follows the channel bottom and
 * naturally ignores higher banks / buildings / bridges baked into the coarse SRTM. The
 * radius bridges SRTM noise; small enough that a sloping river still descends along its
 * length, large enough that a single noisy low pixel doesn't pull the whole surface down.
 */
const WATER_LEVEL_RADIUS_M = 30;
/** Samples per axis on each side of the centre → (2N+1)² lookups per surface point. */
const WATER_LEVEL_SAMPLES = 2;

export interface WaterMesh {
  /** OSM id of the source polygon (or relation) — useful for debugging. */
  id: number;
  /** Representative (minimum) Y of the surface in chunk-local meters. The surface is no
   *  longer flat — vertices follow the local terrain — so this is for debugging only. */
  y: number;
  geometry: MeshGeometry;
}

export function buildWaterGeometry(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): WaterMesh[] {
  const out: WaterMesh[] = [];
  const half = payload.sizeMeters * 0.5;
  const chunkAABB: Rect = { minX: -half, maxX: half, minZ: -half, maxZ: half };

  for (const w of payload.osm.water) {
    const mesh = buildOneWater(w, payload, heightfield, chunkAABB);
    if (mesh) out.push(mesh);
  }

  return out;
}

/**
 * Lower the terrain mesh's Y wherever water covers it, to sit below the (per-vertex) water
 * surface. Without this, SRTM interpolation puts the terrain surface several meters above
 * the water — bridges, shoreline sampling noise, neighbouring buildings — and the opaque
 * terrain hides the water.
 *
 * The carved footprint is the *exact same* outer-minus-island-holes region the water mesh
 * draws (both go through `clipWaterRings` + `isWater`), so the carve can never disagree
 * with the visible water: islands modelled as inner rings keep their terrain, matching
 * what the OSM map (and the minimap) show as land.
 *
 * Mutates `terrainPositions` in place; only ever lowers (never raises) terrain. The
 * heightfield is intentionally not touched: physics colliders and road/bridge construction
 * continue to use raw SRTM, so a car driving over a bridge above water still rides on the
 * real elevation.
 *
 * Call this after `buildTerrain` but before uploading the terrain mesh to the GPU.
 */
export function carveTerrainForWater(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  terrainPositions: Float32Array,
  terrainResolution: number,
): void {
  if (payload.osm.water.length === 0) return;
  const half = payload.sizeMeters * 0.5;
  const chunkAABB: Rect = { minX: -half, maxX: half, minZ: -half, maxZ: half };
  const step = payload.sizeMeters / (terrainResolution - 1);

  for (const w of payload.osm.water) {
    const rings = clipWaterRings(w, payload, chunkAABB);
    if (!rings) continue;
    const { clippedOuter, clippedInners } = rings;

    // Only iterate the polygon's AABB to keep per-vertex work bounded by polygon size.
    const polyAabb = ringAABB(clippedOuter);
    const colLo = Math.max(0, Math.floor((polyAabb.minX + half) / step));
    const colHi = Math.min(terrainResolution - 1, Math.ceil((polyAabb.maxX + half) / step));
    const rowLo = Math.max(0, Math.floor((polyAabb.minZ + half) / step));
    const rowHi = Math.min(terrainResolution - 1, Math.ceil((polyAabb.maxZ + half) / step));

    for (let row = rowLo; row <= rowHi; row++) {
      for (let col = colLo; col <= colHi; col++) {
        const vx = -half + col * step;
        const vz = -half + row * step;
        // Inside the water outline AND not inside an island hole — matches the mesh exactly.
        if (!isWater(vx, vz, clippedOuter, clippedInners)) continue;
        const carveTo = waterYAt(heightfield, vx, vz, chunkAABB) - WATER_CARVE_DEPTH;
        const yIdx = (row * terrainResolution + col) * 3 + 1;
        if (terrainPositions[yIdx]! > carveTo) {
          terrainPositions[yIdx] = carveTo;
        }
      }
    }
  }
}

interface Rect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

type Ring = Array<{ x: number; z: number }>;

interface ClippedWater {
  /** Outer water outline, clipped to the chunk (open ring, ≥3 verts). */
  clippedOuter: Ring;
  /** Island holes, clipped to the chunk. Points inside any of these are land, not water. */
  clippedInners: Ring[];
}

/**
 * Project + clip a water polygon's rings to the chunk rectangle. Returns null if the polygon
 * doesn't meaningfully touch this chunk. Shared by the mesh builder and the terrain carve so
 * both operate on the identical water footprint (outer outline + island holes).
 */
function clipWaterRings(w: OsmWaterArea, payload: ChunkPayload, chunkAABB: Rect): ClippedWater | null {
  const outer = projectRing(w.outer, payload);
  if (outer.length < 3) return null;

  // Cheap AABB reject — skip polygons that don't touch this chunk at all.
  const aabb = ringAABB(outer);
  if (
    aabb.maxX < chunkAABB.minX ||
    aabb.minX > chunkAABB.maxX ||
    aabb.maxZ < chunkAABB.minZ ||
    aabb.minZ > chunkAABB.maxZ
  ) {
    return null;
  }

  const clippedOuter = clipRingToRect(outer, chunkAABB);
  if (clippedOuter.length < 3) return null;

  const clippedInners: Ring[] = [];
  if (w.inners) {
    for (const innerIds of w.inners) {
      const inner = projectRing(innerIds, payload);
      if (inner.length < 3) continue;
      const clipped = clipRingToRect(inner, chunkAABB);
      if (clipped.length >= 3) clippedInners.push(clipped);
    }
  }

  return { clippedOuter, clippedInners };
}

/** A point is water if it's inside the outer outline and outside every island hole. */
function isWater(x: number, z: number, clippedOuter: Ring, clippedInners: Ring[]): boolean {
  if (!pointInPolygon(x, z, clippedOuter)) return false;
  for (const inner of clippedInners) {
    if (pointInPolygon(x, z, inner)) return false;
  }
  return true;
}

function pointInPolygon(x: number, z: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    const intersect =
      a.z > z !== b.z > z && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z || 1e-9) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

function buildOneWater(
  w: OsmWaterArea,
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  chunkAABB: Rect,
): WaterMesh | null {
  const rings = clipWaterRings(w, payload, chunkAABB);
  if (!rings) return null;
  const { clippedOuter, clippedInners } = rings;

  // Triangulate (clipped outer + clipped inners).
  const flat: number[] = [];
  for (const p of clippedOuter) flat.push(p.x, p.z);
  const holeIndices: number[] = [];
  for (const inner of clippedInners) {
    holeIndices.push(flat.length / 2);
    for (const p of inner) flat.push(p.x, p.z);
  }
  const triangles = earcut(flat, holeIndices.length > 0 ? holeIndices : undefined, 2);
  if (triangles.length === 0) return null;

  const vertCount = flat.length / 2;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const uvs = new Float32Array(vertCount * 2);
  let minY = Infinity;
  for (let i = 0; i < vertCount; i++) {
    const x = flat[i * 2]!;
    const z = flat[i * 2 + 1]!;
    // Per-vertex height: the surface drapes over the local terrain (flat on lakes, sloping
    // down rivers) instead of collapsing to one flat plane.
    const y = waterYAt(heightfield, x, z, chunkAABB);
    if (y < minY) minY = y;
    positions[i * 3 + 0] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    // Surface normals stay up: the height variation is gentle and the animated normal map
    // on the water material dominates the shading anyway.
    normals[i * 3 + 0] = 0;
    normals[i * 3 + 1] = 1;
    normals[i * 3 + 2] = 0;
    // UVs scaled to ~10m per tile — matches the bump texture scale on WaterMaterial.
    uvs[i * 2 + 0] = x / 10;
    uvs[i * 2 + 1] = z / 10;
  }
  const indices = new Uint32Array(triangles);

  return {
    id: w.id,
    y: minY,
    geometry: { positions, indices, normals, uvs },
  };
}

/**
 * Water surface height at a point: the minimum terrain over a small neighbourhood, lifted by
 * the surface offset. Sampling the local low point (not one value for the whole polygon) is
 * what lets the surface slope down a river and hug the ground in sliver chunks, while the
 * `min` ignores higher banks/buildings/bridges and the radius smooths SRTM noise. Sample
 * coordinates are clamped into the chunk so `sampleHeightfield` (which returns 0 outside the
 * chunk) can't drag the level down to sea level near edges.
 */
function waterYAt(h: HeightfieldData, x: number, z: number, chunkAABB: Rect): number {
  let minY = Infinity;
  const n = WATER_LEVEL_SAMPLES;
  const r = WATER_LEVEL_RADIUS_M;
  for (let iz = -n; iz <= n; iz++) {
    const sz = clamp(z + (iz / n) * r, chunkAABB.minZ, chunkAABB.maxZ);
    for (let ix = -n; ix <= n; ix++) {
      const sx = clamp(x + (ix / n) * r, chunkAABB.minX, chunkAABB.maxX);
      const y = sampleHeightfield(h, sx, sz);
      if (y < minY) minY = y;
    }
  }
  return minY + WATER_SURFACE_OFFSET;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function projectRing(nodeIds: number[], payload: ChunkPayload): Ring {
  const ring: Ring = [];
  // Skip the duplicate closing node — earcut treats rings as open.
  const end =
    nodeIds.length > 0 && nodeIds[0] === nodeIds[nodeIds.length - 1]
      ? nodeIds.length - 1
      : nodeIds.length;
  for (let i = 0; i < end; i++) {
    const id = nodeIds[i]!;
    const node = payload.osm.nodes[id];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
    ring.push({ x, z });
  }
  return ring;
}

function ringAABB(ring: Ring): Rect {
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (const p of ring) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minZ, maxZ };
}

/**
 * Sutherland–Hodgman polygon clip against an axis-aligned rectangle. Input ring is open
 * (no duplicate closing vertex). Output is also open. Handles convex *or* concave subjects
 * because the clip rect is convex — the algorithm's only invariant.
 */
function clipRingToRect(ring: Ring, rect: Rect): Ring {
  let out: Ring = ring;
  // Left
  out = clipEdge(out, (p) => p.x >= rect.minX, (a, b) => intersectX(a, b, rect.minX));
  if (out.length === 0) return out;
  // Right
  out = clipEdge(out, (p) => p.x <= rect.maxX, (a, b) => intersectX(a, b, rect.maxX));
  if (out.length === 0) return out;
  // Bottom (smaller Z)
  out = clipEdge(out, (p) => p.z >= rect.minZ, (a, b) => intersectZ(a, b, rect.minZ));
  if (out.length === 0) return out;
  // Top (larger Z)
  out = clipEdge(out, (p) => p.z <= rect.maxZ, (a, b) => intersectZ(a, b, rect.maxZ));
  return out;
}

function clipEdge(
  ring: Ring,
  inside: (p: { x: number; z: number }) => boolean,
  intersect: (a: { x: number; z: number }, b: { x: number; z: number }) => { x: number; z: number },
): Ring {
  if (ring.length === 0) return ring;
  const out: Ring = [];
  let prev = ring[ring.length - 1]!;
  let prevInside = inside(prev);
  for (let i = 0; i < ring.length; i++) {
    const cur = ring[i]!;
    const curInside = inside(cur);
    if (curInside) {
      if (!prevInside) out.push(intersect(prev, cur));
      out.push(cur);
    } else if (prevInside) {
      out.push(intersect(prev, cur));
    }
    prev = cur;
    prevInside = curInside;
  }
  return out;
}

function intersectX(
  a: { x: number; z: number },
  b: { x: number; z: number },
  cx: number,
): { x: number; z: number } {
  const dx = b.x - a.x;
  const t = dx === 0 ? 0 : (cx - a.x) / dx;
  return { x: cx, z: a.z + (b.z - a.z) * t };
}

function intersectZ(
  a: { x: number; z: number },
  b: { x: number; z: number },
  cz: number,
): { x: number; z: number } {
  const dz = b.z - a.z;
  const t = dz === 0 ? 0 : (cz - a.z) / dz;
  return { x: a.x + (b.x - a.x) * t, z: cz };
}
